import { FunctionComponent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'
import { IReleaseNote } from '../../../shared/services/release-notes/release-notes-model'
import { IUploadedResource } from '../../../shared/services/uploaded-resource/uploaded-resource.model'
import { Box, ButtonBase, Chip, IconButton, Modal, Paper, Skeleton, Stack, Typography } from '@mui/material'
import { BuildOutlined, ChevronLeft, ChevronRight, Close, DownloadOutlined, InfoOutlined, InsertDriveFileOutlined, NewReleasesOutlined, TrendingUpOutlined } from '@mui/icons-material'
import moment from 'moment'
import UploadedResourceService from '../../../shared/services/uploaded-resource/uploaded-resource.service'
import { renderMarkdownBlocks } from '../display-elements-components/markdown-blocks'
import { releaseNoteTypeLabel } from '../../../shared/constants/release-note-types'

const isImageResource = (resource: IUploadedResource) =>
  (resource.mimetype ?? '').startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(resource.filename ?? '')

const ReleaseNoteTypeChip: FunctionComponent<{ status?: string }> = ({ status }) => {
  const label = releaseNoteTypeLabel(status)
  if (status === 'Feature') {
    return <Chip size='small' color='primary' icon={<NewReleasesOutlined />} label={label} />
  }
  if (status === 'Improvement') {
    return <Chip size='small' color='primary' variant='outlined' icon={<TrendingUpOutlined />} label={label} />
  }
  if (status === 'Fix') {
    return <Chip size='small' variant='outlined' icon={<BuildOutlined />} label={label} />
  }
  return <Chip size='small' variant='outlined' icon={<InfoOutlined />} label={label} />
}

/**
 * One release note, laid out to be read in place: the text is always open and its screenshots
 * sit beside it (below it on narrow screens), so a note never needs a click to be read. Clicking
 * a screenshot opens it full size, with arrows to move through the note's other screenshots.
 * Screenshots are fetched once the note scrolls near the viewport.
 */
export const ReleaseNoteBox: FunctionComponent<{ releaseNote: IReleaseNote }> = ({ releaseNote }) => {
  // In upload order, so the first screenshot uploaded is the one shown large.
  const imageResources = useMemo(
    () => (releaseNote.uploadedResources ?? [])
      .filter(isImageResource)
      .sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? '') || (a.filename ?? '').localeCompare(b.filename ?? '')),
    [releaseNote.uploadedResources])
  const fileResources = useMemo(
    () => (releaseNote.uploadedResources ?? []).filter(resource => !isImageResource(resource)),
    [releaseNote.uploadedResources])

  const rootRef = useRef<HTMLDivElement>(null)
  const [isNearViewport, setIsNearViewport] = useState(false)
  // A url once loaded, null if the download failed (that screenshot is then left out).
  const [imageUrls, setImageUrls] = useState<Record<string, string | null>>({})
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  useEffect(() => {
    if (isNearViewport || imageResources.length === 0) return
    const node = rootRef.current
    if (!node || typeof IntersectionObserver === 'undefined') {
      setIsNearViewport(true)
      return
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setIsNearViewport(true)
        observer.disconnect()
      }
    }, { rootMargin: '400px 0px' })
    observer.observe(node)
    return () => observer.disconnect()
  }, [isNearViewport, imageResources.length])

  useEffect(() => {
    if (!isNearViewport || imageResources.length === 0) return
    let cancelled = false
    const createdUrls: string[] = []
    const service = new UploadedResourceService()
    imageResources.forEach(resource => {
      service.getDownloadBlob(resource.id)
        .then(blob => {
          if (cancelled) return
          const url = URL.createObjectURL(blob)
          createdUrls.push(url)
          setImageUrls(previous => ({ ...previous, [resource.id]: url }))
        })
        .catch(() => {
          if (!cancelled) setImageUrls(previous => ({ ...previous, [resource.id]: null }))
        })
    })
    return () => {
      cancelled = true
      createdUrls.forEach(url => URL.revokeObjectURL(url))
    }
  }, [isNearViewport, imageResources])

  // Screenshots that failed to download are dropped rather than shown as broken images.
  const screenshots = imageResources.filter(resource => imageUrls[resource.id] !== null)
  const loadedScreenshots = screenshots.filter(resource => imageUrls[resource.id])
  const hasScreenshots = screenshots.length > 0

  const openLightbox = (resourceId: string) => {
    const index = loadedScreenshots.findIndex(resource => resource.id === resourceId)
    if (index !== -1) setLightboxIndex(index)
  }
  const stepLightbox = (step: number) => {
    setLightboxIndex(current => current === null
      ? current
      : (current + step + loadedScreenshots.length) % loadedScreenshots.length)
  }
  const lightboxKeyHandler = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowRight') stepLightbox(1)
    if (event.key === 'ArrowLeft') stepLightbox(-1)
  }

  const screenshotAlt = (index: number) => `${releaseNote.title}: screenshot ${index + 1} of ${screenshots.length}`

  const screenshotButton = (resource: IUploadedResource, index: number, isPrimary: boolean) => {
    const url = imageUrls[resource.id]
    return (
      <ButtonBase
        key={resource.id}
        onClick={() => openLightbox(resource.id)}
        disabled={!url}
        aria-label={`Open ${screenshotAlt(index)}`}
        sx={{
          display: 'block',
          width: isPrimary ? '100%' : 112,
          flexShrink: 0,
          aspectRatio: '16 / 10',
          borderRadius: '8px',
          border: 1,
          borderColor: 'divider',
          bgcolor: 'lightGrey.light',
          overflow: 'hidden',
          cursor: 'zoom-in',
          '&:hover img': { opacity: 0.9 },
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
        }}
      >
        {url
          ? <Box component='img' src={url} alt={screenshotAlt(index)} sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }} />
          : <Skeleton variant='rectangular' sx={{ width: '100%', height: '100%' }} />}
      </ButtonBase>
    )
  }

  const lightboxResource = lightboxIndex !== null ? loadedScreenshots[lightboxIndex] : undefined
  const lightboxUrl = lightboxResource ? imageUrls[lightboxResource.id] : undefined

  return (
    <Paper ref={rootRef} component='article' variant='outlined' sx={{ borderRadius: '10px', p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: 'grid',
          gap: { xs: 2, lg: 4 },
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: hasScreenshots ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)' },
          alignItems: 'start',
        }}
      >
        <Box sx={{ minWidth: 0, maxWidth: 760 }}>
          <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 1 }}>
            <ReleaseNoteTypeChip status={releaseNote.status} />
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              {moment(releaseNote.releasedDate).format('D MMMM YYYY')}
            </Typography>
          </Stack>
          <Typography variant='h6' component='h3' sx={{ mb: 1.5, lineHeight: 1.35 }}>{releaseNote.title}</Typography>
          <Box sx={{ '& > :last-child': { mb: 0 } }}>
            {renderMarkdownBlocks(releaseNote.description)}
          </Box>
          {fileResources.length > 0 && (
            <Stack spacing={1} sx={{ mt: 2 }}>
              {fileResources.map(resource => (
                <Box key={resource.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  <InsertDriveFileOutlined fontSize='small' sx={{ color: 'text.secondary' }} />
                  <Typography variant='body2' noWrap sx={{ flex: 1, minWidth: 0 }}>{resource.filename}</Typography>
                  <IconButton
                    size='small'
                    aria-label={`Download ${resource.filename}`}
                    onClick={() => new UploadedResourceService().downloadFile(resource.id, resource.filename)}
                  >
                    <DownloadOutlined fontSize='small' />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
        {hasScreenshots && (
          <Box sx={{ minWidth: 0 }}>
            {screenshotButton(screenshots[0], 0, true)}
            {screenshots.length > 1 && (
              <Stack direction='row' spacing={1} sx={{ mt: 1, overflowX: 'auto', pb: 0.5 }}>
                {screenshots.slice(1).map((resource, index) => screenshotButton(resource, index + 1, false))}
              </Stack>
            )}
          </Box>
        )}
      </Box>
      <Modal open={!!lightboxUrl} onClose={() => setLightboxIndex(null)} aria-label={`${releaseNote.title} screenshots`}>
        <Box
          onClick={() => setLightboxIndex(null)}
          onKeyDown={lightboxKeyHandler}
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            bgcolor: 'rgba(0, 0, 0, 0.85)',
            outline: 'none',
          }}
        >
          <IconButton
            aria-label='Close'
            onClick={() => setLightboxIndex(null)}
            sx={{ position: 'fixed', top: 16, right: 16, color: 'common.white', bgcolor: 'rgba(0, 0, 0, 0.5)', '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' } }}
          >
            <Close />
          </IconButton>
          {loadedScreenshots.length > 1 && (
            <>
              <IconButton
                aria-label='Previous screenshot'
                onClick={(event) => { event.stopPropagation(); stepLightbox(-1) }}
                sx={{ position: 'fixed', left: { xs: 8, sm: 24 }, top: '50%', transform: 'translateY(-50%)', color: 'common.white', bgcolor: 'rgba(0, 0, 0, 0.5)', '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' } }}
              >
                <ChevronLeft fontSize='large' />
              </IconButton>
              <IconButton
                aria-label='Next screenshot'
                onClick={(event) => { event.stopPropagation(); stepLightbox(1) }}
                sx={{ position: 'fixed', right: { xs: 8, sm: 24 }, top: '50%', transform: 'translateY(-50%)', color: 'common.white', bgcolor: 'rgba(0, 0, 0, 0.5)', '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' } }}
              >
                <ChevronRight fontSize='large' />
              </IconButton>
            </>
          )}
          {lightboxUrl && (
            <Box
              component='img'
              src={lightboxUrl}
              alt={screenshotAlt(screenshots.findIndex(resource => resource.id === lightboxResource?.id))}
              onClick={(event) => event.stopPropagation()}
              sx={{ maxWidth: { xs: 'calc(100vw - 32px)', sm: 'calc(100vw - 160px)' }, maxHeight: 'calc(100vh - 120px)', objectFit: 'contain', borderRadius: '8px', bgcolor: 'common.white', cursor: 'default' }}
            />
          )}
          {loadedScreenshots.length > 1 && lightboxIndex !== null && (
            <Typography variant='body2' sx={{ color: 'common.white' }}>
              {lightboxIndex + 1} of {loadedScreenshots.length}
            </Typography>
          )}
        </Box>
      </Modal>
    </Paper>
  )
}
